import { Injectable } from '@angular/core';
import { DateTime, IANAZone } from 'luxon';
import { dateTimeFormat } from './constants';

@Injectable({
  providedIn: 'root',
})
export class DateUtilService {
  constructor() {}

  public formatDateTimeZone(date: Date | null): string {
    if (!date) {
      return '';
    }
    if (
      date.getHours() === 0 &&
      date.getMinutes() === 0 &&
      date.getSeconds() === 0
    ) {
      date.setHours(new Date().getHours());
      date.setMinutes(new Date().getMinutes());
      date.setSeconds(new Date().getSeconds());
    }
    const year = date.getFullYear();
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    const day = ('0' + date.getDate()).slice(-2);
    const hours = ('0' + date.getHours()).slice(-2);
    const minutes = ('0' + date.getMinutes()).slice(-2);
    const seconds = ('0' + date.getSeconds()).slice(-2);
    const timezoneAbbreviation = this.getTimezoneAbbreviation();
    return `${month}/${day}/${year} ${hours}:${minutes}:${seconds} ${timezoneAbbreviation}`;
  }

  public getTimezoneAbbreviation(): string {
    const offset = new Date().getTimezoneOffset();
    const sign = offset > 0 ? '-' : '+';
    const absOffset = Math.abs(offset);
    const hours = Math.floor(absOffset / 60);
    const minutes = absOffset % 60;
    const formattedOffset = `${sign}${('0' + hours).slice(-2)}:${(
      '0' + minutes
    ).slice(-2)}`;
    switch (formattedOffset) {
      case '-05:00':
        return 'EST'; // Eastern Standard Time
      case '-06:00':
        return 'CST'; // Central Standard Time
      case '-07:00':
        return 'MST'; // Mountain Standard Time
      case '-08:00':
        return 'PST'; // Pacific Standard Time
      case '-09:00':
        return 'AKST'; // Alaska Standard Time
      case '-10:00':
        return 'HST'; // Hawaii-Aleutian Standard Time
      case '+05:30':
        return 'IST'; // Indian Standard Time
      default:
        return '';
    }
  }

  public getCurrentTimeInStoredTimezone(): string {
    const currentTime = DateTime.now().setZone(
      toIanaZone(localStorage.getItem('timezone'))
    );

    return (
      currentTime.toISO({
        suppressMilliseconds: false,
        includeOffset: false,
      }) + 'Z'
    );
  }
}

/**
 * Time zone rows carry `name` as "<label> - <IANA id>" (e.g.
 * "Eastern Standard Time - America/New York") and `timeZoneCode` as an
 * abbreviation. Only the IANA id resolves to the right offset in luxon:
 * "AKST" is not a zone at all, and "EST"/"MST"/"HST" are fixed-offset legacy
 * zones that ignore DST.
 */
export function toIanaZone(zoneName?: string | null): string {
  const zone = (zoneName ?? '')
    .split(' - ')
    .pop()
    ?.trim()
    .replace(new RegExp(/\s/, 'g'), '_');

  return zone && IANAZone.isValidZone(zone) ? zone : 'UTC';
}

export function getOffsetFromZoneName(name: string): string {
  return DateTime.local().setZone(toIanaZone(name)).toFormat('ZZ');
}

/**
 * Zone a status-code timestamp is DISPLAYED in: the logged-in user's zone
 * (`timezoneId`, set from the user profile), so every viewer sees their own
 * zone. Falls back to the row's stored zone (the creator's) when the viewer's
 * zone is unknown. The stored instant is never changed.
 */
export function getViewerDisplayTimeZone<T extends { id?: number }>(
  timeZones: T[],
  rowTimeZoneId?: number
): T | undefined {
  const viewerTimeZoneId = +(localStorage.getItem('timezoneId') ?? '0');
  return (
    (viewerTimeZoneId > 0
      ? timeZones.find(zone => zone.id === viewerTimeZoneId)
      : undefined) ?? timeZones.find(zone => zone.id === rowTimeZoneId)
  );
}

export function getStatusCodeInfoTimeFormat(
  dateStr: string,
  timeZone: { name?: string; timeZoneCode?: string }
): string {
  const date = DateTime.fromISO(dateStr).setZone(toIanaZone(timeZone.name));
  return `${date.toFormat(dateTimeFormat.statusCodeDateTime)} ${
    timeZone.timeZoneCode ?? ''
  } {${date.toUTC().toFormat('HH:mm')} ${date.toFormat('ZZ')}}`;
}
